import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import { listLanguages } from '../../api/languages';
import {
  createOrderOfService,
  deleteOrderOfService,
  listOrderOfService,
} from '../../api/orderOfService';
import { API_BASE_URL } from '../../config';

export default function OrderOfServiceSection({ token }) {
  const [entries, setEntries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [languageId, setLanguageId] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [busyEntryId, setBusyEntryId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [entriesData, languagesData] = await Promise.all([
        listOrderOfService(token),
        listLanguages(token),
      ]);
      setEntries(entriesData);
      setLanguages(languagesData);
      setLanguageId((current) => current || languagesData[0]?.language_id || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const languageName = (id) => languages.find((l) => l.language_id === id)?.name || `#${id}`;

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!languageId) {
      setSaveError('Select a language');
      return;
    }
    if (!pdfFile) {
      setSaveError('Select a PDF file');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');
    try {
      const formData = new FormData();
      formData.append('language_id', languageId);
      formData.append('pdf', pdfFile);

      await createOrderOfService(token, formData);
      setPdfFile(null);
      setSaveSuccess('Order of Service uploaded');
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete this ${languageName(entry.language_id)} upload?`)) return;
    setBusyEntryId(entry.order_of_service_id);
    setError('');
    try {
      await deleteOrderOfService(token, entry.order_of_service_id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyEntryId(null);
    }
  };

  return (
    <section>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Order of Service</h2>

      <p className="text-sm text-gray-500 mb-4">
        Upload one PDF per language. The mobile app always shows the most recently
        uploaded PDF for whichever language the user has selected — older uploads for
        that language stay here as history but are no longer shown.
      </p>

      <form
        onSubmit={handleUpload}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-6 space-y-3"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
          <select
            required
            value={languageId}
            onChange={(e) => setLanguageId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Select a language...</option>
            {languages.map((l) => (
              <option key={l.language_id} value={l.language_id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">PDF file</label>
          <input
            required
            type="file"
            accept=".pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            className="w-full text-sm"
          />
        </div>

        {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        {saveSuccess && <p className="text-sm text-indigo-600">{saveSuccess}</p>}

        <button
          type="submit"
          disabled={isSaving || languages.length === 0}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {isSaving ? 'Uploading...' : 'Upload'}
        </button>
      </form>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Language</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Uploaded at</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                  No uploads yet
                </td>
              </tr>
            ) : (
              entries.map((entry) => {
                const isBusy = busyEntryId === entry.order_of_service_id;
                return (
                  <tr key={entry.order_of_service_id}>
                    <td className="px-4 py-2 text-gray-900">{languageName(entry.language_id)}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                      <a
                        href={`${API_BASE_URL}/order-of-service/${entry.order_of_service_id}/download`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-indigo-600 hover:underline"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDelete(entry)}
                        disabled={isBusy}
                        className="text-red-600 hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </section>
  );
}
